import React from 'react'
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  Edit,
  FlipHorizontal,
  ImageIcon,
  Palette,
  SendToBack,
  Shrink,
  Sliders,
  Trash2,
} from 'lucide-react'
import { useIntlayer } from 'react-intlayer'

type FloatingActionBarProps = {
  onHorizontalCenter: () => void
  onVerticalCenter: () => void
  onCenterInDesign: () => void
  onDelete: () => void
  onSendToBack: () => void
  onFlipHorizontal?: () => void
  showFlipHorizontalButton?: boolean
  onColorChange?: () => void
  showColorButton?: boolean
  onTextEdit?: () => void
  showTextEditButton?: boolean
  onImageReplace?: () => void
  showImageReplaceButton?: boolean
  onImageEdit?: () => void
  showImageEditButton?: boolean
  className?: string
}

export const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  onHorizontalCenter,
  onVerticalCenter,
  onCenterInDesign,
  onDelete,
  onSendToBack,
  onFlipHorizontal,
  showFlipHorizontalButton = false,
  onColorChange,
  showColorButton = false,
  onTextEdit,
  showTextEditButton = false,
  onImageReplace,
  showImageReplaceButton = false,
  onImageEdit,
  showImageEditButton = false,
  className = '',
}) => {
  const {
    centerHorizontally,
    centerVertically,
    centerInDesign,
    changeColor,
    editText,
    delete: deleteText,
    replaceImage,
    editImage,
    sendToBack,
    flipHorizontal,
  } = useIntlayer('floatingActionBar')

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      className={`flex items-center gap-2 bg-white rounded-full shadow-lg border border-gray-200 p-2 z-50 ${className}`}
      onClick={handleClick}
      onMouseDown={handleClick}
      onTouchStart={handleClick}
    >
      <button
        onClick={onHorizontalCenter}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
      >
        <AlignCenterVertical className="h-5 w-5 text-gray-700" />
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          {centerHorizontally}
        </span>
      </button>

      <button
        onClick={onVerticalCenter}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
      >
        <AlignCenterHorizontal className="h-5 w-5 text-gray-700" />
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          {centerVertically}
        </span>
      </button>

      <button
        onClick={onCenterInDesign}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
      >
        <Shrink className="h-5 w-5 text-gray-700 rotate-45" />
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          {centerInDesign}
        </span>
      </button>

      {showFlipHorizontalButton && onFlipHorizontal && (
        <button
          onClick={onFlipHorizontal}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
        >
          <FlipHorizontal className="h-5 w-5 text-gray-700" />
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {flipHorizontal}
          </span>
        </button>
      )}

      <div className="w-px h-6 bg-gray-300" />

      <button
        onClick={onSendToBack}
        className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
      >
        <SendToBack className="h-5 w-5 text-gray-700" />
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          {sendToBack || 'Send to Back'}
        </span>
      </button>

      {showColorButton && onColorChange && (
        <>
          <div className="w-px h-6 bg-gray-300" />

          <button
            onClick={onColorChange}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
          >
            <Palette className="h-5 w-5 text-gray-700" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {changeColor}
            </span>
          </button>
        </>
      )}

      {showTextEditButton && onTextEdit && (
        <>
          <div className="w-px h-6 bg-gray-300" />

          <button
            onClick={onTextEdit}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
            data-gtm="edit-text-button"
          >
            <Edit className="h-5 w-5 text-gray-700" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {editText}
            </span>
          </button>
        </>
      )}

      {showImageReplaceButton && onImageReplace && (
        <>
          <div className="w-px h-6 bg-gray-300" />

          <button
            onClick={onImageReplace}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
          >
            <ImageIcon className="h-5 w-5 text-gray-700" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {replaceImage}
            </span>
          </button>
        </>
      )}

      {showImageEditButton && onImageEdit && (
        <>
          <div className="w-px h-6 bg-gray-300" />

          <button
            onClick={onImageEdit}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors relative group"
          >
            <Sliders className="h-5 w-5 text-gray-700" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {editImage}
            </span>
          </button>
        </>
      )}

      <div className="w-px h-6 bg-gray-300" />

      <button
        onClick={onDelete}
        className="p-2 rounded-full hover:bg-red-50 transition-colors relative group"
        data-gtm="delete-element-button"
      >
        <Trash2 className="h-5 w-5 text-red-600" />
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          {deleteText}
        </span>
      </button>
    </div>
  )
}
